import { useState,useRef,useEffect } from 'react';
import { Header } from './components/Header';
import { MainSlider } from './components/MainSlider';
import { Sidebar } from './components/Sidebar';
import { ImageLoader } from './components/ImageLoader';
import { Hotspot } from './types';
import { Drawer } from "flowbite-react";
import { ArrowLeft } from 'lucide-react';
import { useWindowSize } from "@uidotdev/usehooks";
import { useDebounce } from "@uidotdev/usehooks";
import VideoModal from './components/VideoModal';
import { slideData } from './data';
  

function App() {

  const [activeSlide, setActiveSlide] = useState(0);
  const [activeHotspot, setActiveHotspot] = useState<{ slideIndex: number; hotspotIndex: number } | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const [hotspotData, setHotspotData] = useState<Hotspot | null>(null);

  const windowDimensions = useWindowSize();
  const windowSize = useDebounce(windowDimensions, 250);
  const isLargeDevice = windowSize?.width && windowSize.width > 1023 ? true : false;
    
  const [sidebarWidth, setSidebarWidth] = useState('100vw');
  
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const sidebarRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    function updateHeight() {
      const sidebarWidth = isLargeDevice && sidebarRef.current?.offsetWidth ? `${sidebarRef.current.offsetWidth}px` : '100vw';
      setSidebarWidth(sidebarWidth);
    }
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [windowSize,isLargeDevice]);

  const hideSlideDrawer = () => {
    setIsOpen(false);
    setHotspotData(null);
    document.body.classList.remove('no-scroll');
  }

  const handleSlideChange = (slideIndex: number) => {
    const sameSlide = slideIndex === hotspotData?.parentIndex;
    if (!sameSlide) {
      hideSlideDrawer();
    }
    setActiveSlide(slideIndex);
  }
  const handleHotspotClick = (slideIndex: number, hotspot: Hotspot, hotspotIndex: number) => {
    hideSlideDrawer();
    // console.log('Hotspot clicked:', { slideIndex, hotspot });
    setActiveSlide(slideIndex);
    setActiveHotspot({ slideIndex, hotspotIndex });
    setIsOpen(true);
    setHotspotData(hotspot);
    document.body.classList.add('no-scroll');
  };

  const handleHotspotHover = (slideIndex: number, hotspotIndex: number) => {
    setActiveHotspot({ slideIndex, hotspotIndex });
  };

  const handleHotspotLeave = () => {
    setActiveHotspot(null);
  };

  

  return (
    <div>
      
      <Header />
      
      <main className="lg:container mx-auto px-4">

        {slideData && (
        <div className="grid grid-cols-1 lg:grid-cols-3 lg:gap-8">
          
          <div className="lg:col-span-2">
            <div id="main-slider" className="lg:h-full" ref={mainContainerRef}>
              <MainSlider
                slides={slideData}
                onSlideChange={handleSlideChange}
                onHotspotClick={handleHotspotClick}
                onHotspotHover={handleHotspotHover}
                onHotspotLeave={handleHotspotLeave}
                activeSlideIndex={activeSlide}
              />
            </div>
          </div>
          
          <div className="lg:relative right-0 lg:col-span-1 overflow-hidden" ref={sidebarRef}>
            <Sidebar
              slides={slideData}
              activeSlide={activeSlide}
              activeHotspot={activeHotspot}
              onHotspotClick={handleHotspotClick}
              onHotspotHover={handleHotspotHover}
              onHotspotLeave={handleHotspotLeave}
            />

            <Drawer open={isOpen} onClose={hideSlideDrawer} backdrop={false} position="right" style={{ width:sidebarWidth }} theme={{
              root: {
                base: 'fixed lg:absolute bg-white z-40 h-screen transition-all overflow-hidden overflow-y-auto hide-scrollbars',
                position: {
                  right: {
                    on: "top-0 left-0 h-full w-full h-screen translate-x-half opacity-100 duration-300 lg:ml-px pt-6 lg:pb-24",
                    off: "top-0 right-0 h-full w-full h-screen translate-x-full opacity-0 duration-300 lg:ml-px pt-6 lg:pb-24"
                  }
                }
              },
              items: {
                base: 'px-8 lg:pr-4 xl:pr-px'
              }
            }}>
              <div className="overflow-hidden overflow-y-scroll hide-scrollbars">
                <Drawer.Items>

                    <button className='flex items-center gap-1 text-primary-dark' onClick={hideSlideDrawer}><ArrowLeft size={16} strokeWidth={2} absoluteStrokeWidth={true} color={'#2678AB'}/> Back</button>

                    {/* <p className='font-mono text-red-600'>
                    sidebarWidth: {sidebarWidth}
                    <br/>
                    drawerHeight: {drawerHeight}
                    </p> */}
                    
                    <div className='mt-4 md:mt-8 lg:mt-12'>

                    {hotspotData?.number &&
                      <div className="mb-4 w-8 h-8 rounded-full bg-tertiary-light flex items-center justify-center text-primary-dark text-sm">
                      {hotspotData.number}
                      </div>
                    }

                    {hotspotData?.title &&
                    <h4 className="text-xl lg:text-2xl text-primary-dark font-medium mb-4">{hotspotData.title}</h4>
                    }
                    
                    {hotspotData?.content &&
                    <div className='paragraph-text' dangerouslySetInnerHTML={{__html: hotspotData?.content}}></div>
                    }

                    {hotspotData?.link && hotspotData?.linkTitle &&
                      <div className="my-6">
                        <a href={hotspotData.link} target="_blank" className="bg-primary-light hover:bg-white border border-primary-light text-white hover:text-primary rounded-md text-sm p-4 mt-4 w-full flex justify-center items-center transition-colors duration-300">{hotspotData.linkTitle}</a>
                      </div>
                    }

                    {hotspotData?.videos && hotspotData.videos.map((videoId, index) => (
                      <div key={index} className="mb-4">
                        <VideoModal videoId={videoId} title={hotspotData.title} />
                      </div>
                    ))}

                    {hotspotData?.images && hotspotData.images.map((image, index) => (
                      <div key={index} className="mb-4">
                        <ImageLoader source={image} /> 
                      </div>
                    ))}

                    </div>
                </Drawer.Items>
              </div>
            </Drawer>
            
          </div>

        </div>
        )}
        
      </main>

    </div>
    
  );
}

export default App;